/** Saved-view config normalization for dirty-detection (Views v2 / E3.5).
 *
 * A view's config is {columnState, filterModel, quickFilter}. AG-Grid's columnState churns on
 * cosmetic re-layout (flex-derived widths jitter, key order varies), so a naive deep-compare would
 * flag "unsaved changes" the instant the grid renders. normalizeViewConfig strips that noise and
 * keeps only the operator-meaningful bits (column order, visibility, pin, sort, fixed widths,
 * filters, quick-filter). viewConfigKey stringifies it with recursively-sorted object keys so the
 * comparison is order-stable — column ORDER stays meaningful (arrays aren't sorted). isConfigDirty
 * compares two configs by that key. Pure + unit-tested; it's the adversarial-review target for
 * false-positive dirtiness.
 */
interface ColState {
  colId?: string;
  hide?: boolean;
  pinned?: string | null;
  sort?: string | null;
  sortIndex?: number | null;
  width?: number;
  flex?: number | null;
}

// R3 (spec §5.4): the AI-Updates filter. 'new' = proposed-record rows (D8); 'updated' = rows with
// field proposals (M3.4); 'all' = either; 'off' = no AI filter. Replaces the old proposalsOnly bool.
export type ProposalsFilter = 'off' | 'new' | 'updated' | 'all';
export type SavedValueMode = 'current' | 'ai';

// Read the AI-Updates filter from a (possibly legacy) view config. Legacy views stored a boolean
// `proposalsOnly` whose true value filtered to rows with field proposals → maps to 'updated'.
export function readProposalsFilter(config: unknown): ProposalsFilter {
  const c = (config ?? {}) as Record<string, unknown>;
  const f = c.proposalsFilter;
  if (f === 'new' || f === 'updated' || f === 'all' || f === 'off') return f;
  if (c.proposalsOnly === true) return 'updated';
  return 'off';
}

/** Read Current/AI from a saved view. Pre-persistence views that select New/Both proposals need
 * AI mode or Current would hide the proposed rows the view explicitly asks for. */
export function readValueMode(config: unknown): SavedValueMode {
  const c = (config ?? {}) as Record<string, unknown>;
  if (c.valueMode === 'current' || c.valueMode === 'ai') return c.valueMode;
  const proposals = readProposalsFilter(c);
  return proposals === 'new' || proposals === 'all' ? 'ai' : 'current';
}

/** Current hides whole-record proposals, so a New/Both filter cannot retain those scopes in Current
 * mode without producing a misleading empty/partial result. Preserve the compatible Updated part. */
export function cohereAiViewState(
  proposalsFilter: ProposalsFilter, valueMode: SavedValueMode,
): { proposalsFilter: ProposalsFilter; valueMode: SavedValueMode } {
  if (valueMode === 'current' && proposalsFilter === 'new') {
    return { proposalsFilter: 'off', valueMode };
  }
  if (valueMode === 'current' && proposalsFilter === 'all') {
    return { proposalsFilter: 'updated', valueMode };
  }
  return { proposalsFilter, valueMode };
}

export function normalizeViewConfig(config: unknown): {
  columns: Array<Record<string, unknown>>;
  filterModel: unknown;
  quickFilter: string;
  filterTree: unknown;
  searchScope: string;
  proposalsFilter: ProposalsFilter;
  valueMode: SavedValueMode;
} {
  const c = (config ?? {}) as Record<string, unknown>;
  const raw = Array.isArray(c.columnState) ? (c.columnState as ColState[]) : [];
  const columns = raw.map((s) => ({
    colId: s.colId,
    hide: !!s.hide,
    pinned: s.pinned ?? null,
    sort: s.sort ?? null,
    sortIndex: s.sortIndex ?? null,
    // a flex column's width is derived (it jitters on relayout) — only a fixed width is meaningful
    width: s.flex ? null : (s.width ?? null),
  }));
  return {
    columns,
    filterModel: c.filterModel ?? {},
    quickFilter: typeof c.quickFilter === 'string' ? c.quickFilter : '',
    // Slice 9 / R3: the FE filter builder + search-scope + AI-Updates filter are view state too. An
    // empty tree / defaults normalize to a stable form so they don't register as dirty on a fresh
    // load. readProposalsFilter folds the legacy `proposalsOnly` boolean into the new enum.
    filterTree: c.filterTree ?? null,
    searchScope: typeof c.searchScope === 'string' ? c.searchScope : 'anywhere',
    proposalsFilter: readProposalsFilter(c),
    valueMode: readValueMode(c),
  };
}

// recursively sort object keys (NOT arrays — array order is meaningful, e.g. column order) so the
// stringified form is stable regardless of key insertion order (AG filterModel key order varies).
function stableSort(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(stableSort);
  if (v && typeof v === 'object') {
    const o = v as Record<string, unknown>;
    return Object.keys(o).sort().reduce<Record<string, unknown>>((acc, k) => {
      acc[k] = stableSort(o[k]);
      return acc;
    }, {});
  }
  return v;
}

export function viewConfigKey(config: unknown): string {
  return JSON.stringify(stableSort(normalizeViewConfig(config)));
}

export function isConfigDirty(current: unknown, baseline: unknown): boolean {
  return viewConfigKey(current) !== viewConfigKey(baseline);
}

/**
 * The view a list OPENS on: the caller's OWN default first, else the list's admin-set TEAM default,
 * else none (today's no-default behavior). Personal always wins — owner ruling 2026-08-10, spec
 * `docs/specs/strategic-architecture/crm-team-default-view-spec.md` §3 rule 1.
 *
 * A pure function so the precedence is testable at all: `CrmListGrid`, which applies it, cannot be
 * rendered in jsdom (~24 useCrm hooks plus a live AgGridReact).
 *
 * `is_default` is now strictly the caller's own — the server used to project it off the row
 * regardless of owner, so a shared view that happened to be its OWNER's default arrived flagged for
 * everyone and this precedence could silently apply a stranger's layout.
 */
export function resolveDefaultView<T extends { is_default: boolean; is_team_default: boolean }>(
  views: readonly T[],
): T | undefined {
  return views.find((v) => v.is_default) ?? views.find((v) => v.is_team_default);
}
